import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I move more units from my Colorado independent dealership?",
    a: "The fastest path is an inventory-integrated website (live feed from vAuto, Frazer, DealerCenter, etc.) plus local SEO so your individual VDP pages rank for the specific year/make/model searches buyers run. We pair that with Google Ads tuned to your strongest segments — trucks, off-road, BHPH, classic — instead of paying Cars.com for shared leads.",
  },
  {
    q: "Will you integrate our DMS / inventory feed?",
    a: "Yes. We integrate with vAuto, Frazer, DealerCenter, Wayne Reaves, Auto Manager, and most major DMS exports so inventory pages stay current automatically — no more stale 'sold' units sitting on the site.",
  },
  {
    q: "Can we compete with Cars.com, AutoTrader, and CarGurus?",
    a: "Yes — by ranking your own VDP (vehicle detail) pages organically and running tighter Google Ads than the listing portals. The portals win on raw inventory volume; you win by owning the local Map Pack and converting buyers directly instead of paying for the lead.",
  },
  {
    q: "Do you handle BHPH (Buy-Here-Pay-Here) marketing?",
    a: "Yes. BHPH is a totally different buyer with very different search behavior and intent. We build dedicated landing pages, application flows, credit pre-qualification forms, and Spanish-language pages where it makes sense for your market.",
  },
  {
    q: "How long does dealership SEO take to work?",
    a: "Most independent dealerships see meaningful traffic and Map Pack movement in 90–180 days. Inventory-page rankings start appearing within weeks because you're targeting long-tail year/make/model queries that aren't dominated by the major portals.",
  },
];

export const Route = createFileRoute("/auto-dealer-marketing-colorado")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () =>
    industryMarketingHead("Auto Dealer", "auto dealer", "auto-dealer-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Auto Dealer"
      industryLower="independent auto dealer"
      slug="auto-dealer-marketing-colorado"
      subheadline="We help Colorado independent dealerships move more units — without paying Cars.com and AutoTrader for every shared lead."
      painPoints={[
        "Cars.com, AutoTrader, and CarGurus capture all the high-intent traffic",
        "Inventory feed updates break the website every other week",
        "Franchise dealers dominate the Map Pack on every used-car search",
      ]}
      serviceDescriptions={{
        seo: "Rank your VDP pages and segment landing pages for trucks, lifted 4x4s, BHPH, classic, AWD, and your strongest makes/models.",
        googleMaps: "Dominate the local Map Pack so 'used car dealership near me' searches come to your lot first.",
        webDesign: "Fast, mobile-first dealership sites with live DMS-integrated inventory, financing tools, and trade-in calculators.",
        ppc: "Profitable Google Ads tuned to your strongest segments — not generic 'used cars' clicks Cars.com already owns.",
        geoAi: "Get cited when Coloradans ask ChatGPT or Gemini for an honest local dealership recommendation.",
        socialMedia: "Real-unit walkaround video that converts price shoppers into showroom visits.",
      }}
      testimonial={{
        quote:
          "Our inventory pages now rank above Cars.com on most year/make/model searches. We finally own our own leads.",
        role: "Independent Dealership Owner",
        city: "Pueblo",
      }}
      faqs={FAQS}
      cityHubLinks={[
        {
          to: "/colorado-springs",
          label: "Auto dealer marketing in Colorado Springs",
          description: "Help Springs lots win the 4x4 and lifted-truck buyer driven by military and outdoor demand.",
        },
        {
          to: "/pueblo-digital-marketing",
          label: "Auto dealer marketing in Pueblo",
          description: "Build BHPH and credit-challenged buyer flow across southern Colorado.",
        },
      ]}
    />
  ),
});
